import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faFileInvoiceDollar, faDownload, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Listing/Table';
import Button from '../../components/Form/Button';
import Modal from '../../components/Modal/Modal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

const PLAN_LABELS = { MONTHLY: 'Mensuel', YEARLY: 'Annuel' };

const fmtAmount = (n) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' dhs';

export default function BackofficeSubscriptions() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [rejecting, setRejecting] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        return axios.get(`${API_BASE_URL}/api/v1/backoffice/subscriptions`, { withCredentials: true })
            .then(({ data }) => setRequests(data))
            .catch(() => toast.error('Impossible de charger les demandes.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Backoffice — Abonnements';
        load();
    }, [load]);

    useEffect(() => {
        setTitle('Abonnements');
        setTitleDescription('Demandes de virement bancaire en attente de vérification');
        setTitleIcon('ti-file-invoice');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const handleApprove = (row) => {
        axios.patch(`${API_BASE_URL}/api/v1/backoffice/subscriptions/${row.invoiceId}/approve`, null, { withCredentials: true })
            .then(() => {
                toast.success('Abonnement activé.');
                load();
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const submitReject = () => {
        if (!rejectReason.trim()) { toast.error('Veuillez indiquer un motif.'); return; }
        axios.patch(`${API_BASE_URL}/api/v1/backoffice/subscriptions/${rejecting.invoiceId}/reject`, { reason: rejectReason.trim() }, { withCredentials: true })
            .then(() => {
                toast.success('Demande rejetée.');
                setRejecting(null);
                setRejectReason('');
                load();
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const columns = [
        { accessor: 'pharmacyName', Header: 'Pharmacie', sortable: true, Cell: (row) => <span style={{ fontWeight: 600 }}>{row.pharmacyName}</span> },
        { accessor: 'subscriptionPlan', Header: 'Plan', Cell: (row) => PLAN_LABELS[row.subscriptionPlan] || row.subscriptionPlan },
        { accessor: 'amount', Header: 'Montant', Cell: (row) => fmtAmount(row.amount) },
        { accessor: 'reference', Header: 'Référence', Cell: (row) => <span style={{ fontFamily: 'monospace' }}>{row.reference}</span> },
        {
            accessor: 'submittedAt',
            Header: 'Soumis le',
            sortable: true,
            Cell: (row) => new Date(row.submittedAt).toLocaleString('fr-FR'),
        },
        {
            accessor: 'hasProof',
            Header: 'Justificatif',
            Cell: (row) => row.hasProof
                ? (
                    <a
                        href={`${API_BASE_URL}/api/v1/backoffice/subscriptions/${row.invoiceId}/proof`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <FontAwesomeIcon icon={faDownload} className="me-1" /> Voir
                    </a>
                )
                : <span className="text-muted">—</span>,
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown" onClick={(e) => e.stopPropagation()}>
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <Button className="dropdown-item" onConfirm={() => handleApprove(row)}>
                                <FontAwesomeIcon icon={faCheck} className="me-2 text-success" /> Approuver
                            </Button>
                        </li>
                        <li>
                            <button className="dropdown-item text-danger" onClick={() => { setRejecting(row); setRejectReason(''); }}>
                                <FontAwesomeIcon icon={faTimes} className="me-2" /> Rejeter
                            </button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={requests}
                mode="client"
                loading={loading}
                keyword={keyword}
                setKeyword={setKeyword}
                searchPlaceholder="Rechercher une pharmacie..."
                rowId="invoiceId"
                pageSize={20}
                emptyMessage="Aucune demande en attente"
            />

            <Modal
                show={!!rejecting}
                onClose={() => setRejecting(null)}
                title={`Rejeter la demande — ${rejecting?.pharmacyName || ''}`}
                icon={faFileInvoiceDollar}
                footer={
                    <Button className="modal-btn-danger" onClick={submitReject}>
                        Confirmer le rejet
                    </Button>
                }
            >
                <label className="cdm-field-label">Motif du rejet</label>
                <textarea
                    className="form-control"
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ex : référence introuvable sur le relevé bancaire"
                />
            </Modal>
        </>
    );
}
